import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import { isEmpty, isNil, uniq } from 'lodash'
import { IconFolder, IconSearch } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { ExternalLink } from '@/components/ExternalLink'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { TextInput } from '@/components/forms/TextInput'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { Loader } from '@/components/loaders/Loader'
import NoData from '@/components/NoData'
import { Pill } from '@/components/pills/Pill'
import { TreeTable } from '@/components/tables/TreeTable/TreeTable'
import { toast } from '@/components/toasts/toast.helpers'
import {
  DRIVE_NOT_FOUND,
  INVALID_CURSOR,
  NOT_SYNCED,
  REMOVAL_INCOMPLETE,
  fetchConnectionDrives,
  fetchConnectionItems,
  finishDriveRemoval,
  saveBlockedItems,
} from '@/reducers/organizations/documentSourceConnection.actions'
import { dateFormatMoment } from '@/utils/constants'
import { formatTime } from '@/utils/helpers'
import {
  EMPTY_CACHE,
  NOT_SYNCED_NOTICE,
  ROW_KIND,
  appendPage,
  browseRowsOf,
  changesPayload,
  completeChildrenOf,
  fileTypeOf,
  footerOf,
  markListing,
  recordChange,
  searchRowsOf,
  searchTreeOf,
  syncedRootsOf,
  toggleTarget,
} from '@/views/Organizations/components/ManageFilesModal.helpers'
import { useManageFilesSearch } from '@/views/Organizations/components/hooks/useManageFilesSearch'

const TITLE = 'Manage files'
const DRIVES_FAILED_COPY = "Couldn't load this connection's files. Close and reopen to retry."
const SAVED_COPY =
  'Saved. Blocked files were removed from AI Answers. Unblocked files return after the next nightly sync.'
const REMOVAL_INCOMPLETE_COPY =
  "Saved, but removal from AI Answers isn't finished. Select Finish removal."
const REMOVAL_INCOMPLETE_CLOSED_COPY =
  "Saved, but removal from AI Answers isn't finished. Open Manage files and select Finish removal."
const SAVE_FAILED_COPY = "Couldn't save your changes. Try again."
const LIBRARY_CHANGED_COPY =
  "Couldn't save: a library changed while Manage files was open. Close Manage files and open it again to load the latest files."
const LIBRARY_CHANGED_CLOSED_COPY =
  "Couldn't save: a library changed while Manage files was open. Open Manage files again to load the latest files."
const DISCARD_TITLE = 'Discard unsaved changes?'
const DISCARD_BODY = "Your unsaved changes to this connection's files will be lost."
const DISCARD_LABEL = 'Discard changes'
const NO_DATE = '—'

const MAX_SEARCH_LENGTH = 200
const SEARCH_LABEL = 'Search files and folders'
const SEARCHING_LABEL = 'Searching…'
const SEARCH_FAILED_COPY = "Couldn't search. Try again."
const TRUNCATED_COPY = 'Showing the first 200 matches. Refine your search to see more.'
const NO_MATCHES_COPY = 'No files or folders match your search.'

const FINISH_REMOVAL_LABEL = 'Finish removal'
const REMOVAL_PENDING_NOTICE = "Removal from AI Answers isn't finished."
const REMOVAL_FINISHED_COPY = 'Removal finished.'
const STILL_PENDING_COPY = "Removal from AI Answers still isn't finished. Try Finish removal again."
const FINISH_FAILED_COPY = "Couldn't finish removal. Try again."

export const FILE_COLUMNS = [
  { accessor: 'nameCell', label: 'Name' },
  { accessor: 'modifiedCell', label: 'Last modified' },
  { accessor: 'statusCell', label: 'Status' },
  { accessor: 'openCell', label: 'Open' },
]

const statusCellOf = (model, finishRemovalCell) => {
  if (model.kind === ROW_KIND.ROOT) {
    if (model.removalPending) {
      return finishRemovalCell(model)
    }
    return isNil(model.notice) ? null : <span className="muted-text">{model.notice}</span>
  }
  if (isNil(model.status)) {
    return null
  }

  const { label, variant, tooltip } = model.status
  return (
    <Pill small tooltip={tooltip} {...(isNil(variant) ? {} : { [variant]: true })}>
      {label}
    </Pill>
  )
}

const FOLDER_TYPE = { icon: IconFolder, tone: null }

export const cellsOf = (model, finishRemovalCell) => {
  if (model.kind === ROW_KIND.NOTICE) {
    return { nameCell: <span className="muted-text">{model.notice}</span> }
  }

  const isFile = model.kind === ROW_KIND.ITEM && !model.item.is_folder
  const { icon: NameIcon, tone } = isFile ? fileTypeOf(model.item.name) : FOLDER_TYPE
  return {
    nameCell: (
      <>
        <NameIcon className={classNames('icon-svg', { [`file-icon-${tone}`]: !isNil(tone) })} />
        <span>{model.label}</span>
      </>
    ),
    modifiedCell: isNil(model.lastModified)
      ? NO_DATE
      : formatTime(model.lastModified, dateFormatMoment.short),
    statusCell: statusCellOf(model, finishRemovalCell),
    openCell: isNil(model.webUrl) ? null : (
      <ExternalLink url={model.webUrl} aria-label={`Open ${model.label}`} />
    ),
  }
}

// A library that left the connection or lost its sync rejects every retry until a reload.
const isLibraryChanged = (error) => [DRIVE_NOT_FOUND, NOT_SYNCED].includes(error.kind)

const saveFailureCopy = (error) =>
  isLibraryChanged(error) ? LIBRARY_CHANGED_COPY : error.message ?? SAVE_FAILED_COPY

// A save can outlive the modal (it completes server-side), so its outcome becomes a toast.
const toastSaveOutcome = (error) => {
  if (isNil(error)) {
    toast.success(SAVED_COPY)
    return
  }
  // The server's copy points at a Finish removal button that is no longer on screen.
  if (error.kind === REMOVAL_INCOMPLETE) {
    toast.warning(REMOVAL_INCOMPLETE_CLOSED_COPY)
    return
  }
  toast.error(isLibraryChanged(error) ? LIBRARY_CHANGED_CLOSED_COPY : saveFailureCopy(error))
}

// The server's removal_incomplete message is the save's copy, so a finish says its own.
const finishFailureCopy = (error) =>
  error.kind === REMOVAL_INCOMPLETE ? STILL_PENDING_COPY : FINISH_FAILED_COPY

const toastFinishOutcome = (error) => {
  if (isNil(error)) {
    toast.success(REMOVAL_FINISHED_COPY)
    return
  }
  if (error.kind === REMOVAL_INCOMPLETE) {
    toast.warning(STILL_PENDING_COPY)
    return
  }
  toast.error(FINISH_FAILED_COPY)
}

/**
 * Manage files: the libraries, drives and folders a knowledge-base row covers, as a lazy
 * tree whose checkboxes include files and folders in AI Answers or exclude them. A search
 * swaps the tree for its matches inside their folders. Clicks in either are recorded
 * locally and Update saves them in one request. A drive whose saved exclusions are not yet
 * removed from AI Answers offers Finish removal.
 *
 * @param {number} props.connectionId the document source connection whose files are shown
 * @param {string} props.subtitle what the row covers, e.g. "library Documents in /sites/Knowledge"
 * @param {ManageFilesRoot[]} props.roots the tree's top-level nodes (typedef in
 *   ManageFilesModal.helpers.js)
 * @param {Function} props.onClose closes the modal; called after a save, at once when nothing
 *   is unsaved or a save is running, or once the user confirms discarding unsaved changes; a
 *   save or Finish removal still running then reports its outcome as a toast
 */
export const ManageFilesModal = ({ connectionId, subtitle, roots, onClose }) => {
  const dispatch = useDispatch()
  const [drives, setDrives] = useState(null)
  const [drivesFailed, setDrivesFailed] = useState(false)
  const [cache, setCache] = useState(EMPTY_CACHE)
  const [changes, setChanges] = useState([])
  const [message, setMessage] = useState(null)
  const [saving, setSaving] = useState(false)
  // The drive whose Finish removal is running; one runs at a time, as the API serializes them.
  const [finishing, setFinishing] = useState(null)
  // Remounts the tree, collapsing it, once a save has changed what the server holds.
  const [treeVersion, setTreeVersion] = useState(0)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const { query, setQuery, debouncedQuery, inSearchMode, searchResult } = useManageFilesSearch({
    connectionId,
    roots,
    drives,
    treeVersion,
  })
  // Bumped with treeVersion, so a page fetched before that reset never lands in the new cache.
  const cacheGeneration = useRef(0)
  // Only the latest drives refetch lands, so a slower earlier one can't undo a newer outcome.
  const drivesGeneration = useRef(0)
  const mounted = useRef(true)

  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  const refreshDrives = async () => {
    drivesGeneration.current += 1
    const generation = drivesGeneration.current
    const result = await dispatch(fetchConnectionDrives({ connectionId }))
    const latest = mounted.current && generation === drivesGeneration.current
    if (latest && isNil(result.error)) {
      setDrives(result.data)
    }
    return result
  }

  useEffect(() => {
    refreshDrives().then((result) => {
      if (mounted.current && !isNil(result.error)) {
        setDrivesFailed(true)
      }
    })
  }, [])

  const loadChildren = async (loadTarget, cursor = null) => {
    const { parentKey, driveId, parentId, ancestorIds } = loadTarget
    const listing = cache.listings[parentKey]
    // Expanding fetches a first page only when there is none, or the last attempt failed.
    if (listing?.loading || (isNil(cursor) && !isNil(listing) && isNil(listing.error))) {
      return
    }

    const generation = cacheGeneration.current
    setCache((current) => markListing(current, parentKey, { loading: true }))
    const fetchPage = (pageCursor) =>
      dispatch(fetchConnectionItems({ connectionId, driveId, parentId, cursor: pageCursor }))
    const result = await fetchPage(cursor)
    // A Show more cursor goes stale when the item it points at is deleted: restart once.
    const stale = !isNil(cursor) && result.error?.kind === INVALID_CURSOR
    const { data, error } = stale ? await fetchPage(null) : result
    if (!mounted.current || generation !== cacheGeneration.current) {
      return
    }

    // Functional updates, so pages for different folders land in whichever order they arrive.
    // A first page starts the listing over: the guard lets it through only when missing or failed.
    if (isNil(error)) {
      const replace = isNil(cursor) || stale
      setCache((current) => appendPage(current, { parentKey, driveId, ancestorIds, replace }, data))
    } else {
      setCache((current) => markListing(current, parentKey, { error }))
    }
  }

  const toggle = (item) => {
    const childrenOf = completeChildrenOf(cache)
    setChanges((current) =>
      recordChange(current, item, toggleTarget(item, current, childrenOf), childrenOf)
    )
  }

  const save = async () => {
    const saved = changes
    setMessage(null)
    setSaving(true)
    const { error } = await dispatch(
      saveBlockedItems({ connectionId, changes: changesPayload(saved) })
    )
    if (!mounted.current) {
      toastSaveOutcome(error)
      return
    }
    setSaving(false)

    if (isNil(error)) {
      toast.success(SAVED_COPY)
      onClose()
      return
    }
    if (error.kind !== REMOVAL_INCOMPLETE) {
      setMessage({ text: saveFailureCopy(error), warning: false })
      return
    }

    // The change was saved; only removing newly blocked files from AI Answers is unfinished.
    const savedDriveIds = uniq(saved.map((change) => change.drive_id))
    setChanges([])
    setCache(EMPTY_CACHE)
    cacheGeneration.current += 1
    setTreeVersion((version) => version + 1)
    setMessage({ text: error.message ?? REMOVAL_INCOMPLETE_COPY, warning: true })
    setDrives((current) =>
      current.map((drive) =>
        savedDriveIds.includes(drive.drive_id) ? { ...drive, removal_pending: true } : drive
      )
    )
    refreshDrives()
  }

  const finishRemoval = async (driveId) => {
    setMessage(null)
    setFinishing(driveId)
    const { error } = await dispatch(finishDriveRemoval({ connectionId, driveId }))
    if (!mounted.current) {
      toastFinishOutcome(error)
      return
    }
    setFinishing(null)

    if (!isNil(error)) {
      setMessage({ text: finishFailureCopy(error), warning: error.kind === REMOVAL_INCOMPLETE })
      return
    }

    toast.success(REMOVAL_FINISHED_COPY)
    // Cleared here as well, so the notice goes even when the drives refetch fails.
    setDrives((current) =>
      current.map((drive) =>
        drive.drive_id === driveId ? { ...drive, removal_pending: false } : drive
      )
    )
    refreshDrives()
  }

  // Mid-save the tree is locked, so the only changes are the ones being saved: nothing is lost.
  const requestClose = () => {
    if (saving || isEmpty(changes)) {
      onClose()
      return
    }
    setConfirmingDiscard(true)
  }

  // Removal is per drive, so every root in the finishing drive spins; the test id is per row.
  const finishRemovalCell = ({ key, root }) => (
    <div className="flex-align-center medium-gap">
      <span className="muted-text">{REMOVAL_PENDING_NOTICE}</span>
      <Button
        small
        loading={finishing === root.driveId}
        disabled={saving || !isNil(finishing)}
        data-testid={`finish-removal-${key}`}
        onClick={() => finishRemoval(root.driveId)}
      >
        {FINISH_REMOVAL_LABEL}
      </Button>
    </div>
  )

  const toTreeRows = (models) =>
    models.map((model) => ({
      ...model,
      ...cellsOf(model, finishRemovalCell),
      // A save sends the changes as they were when Update was pressed; later clicks would be lost.
      checkboxDisabled: saving || model.checkboxDisabled,
      childRows: toTreeRows(model.childRows),
      onShowMore: isNil(model.nextCursor)
        ? undefined
        : () => loadChildren(model.loadTarget, model.nextCursor),
    }))

  // Browse and search share `toggle` and `changes`, so a click in one shows in the other.
  const treeProps = {
    rowKey: 'key',
    columns: FILE_COLUMNS,
    checkboxLabel: (row) => `Include ${row.label}`,
    onRowToggle: (row) => toggle(row.item),
  }

  const renderSearchResults = () => {
    if (isEmpty(syncedRootsOf(roots, drives))) {
      return <NoData hideHeader text={NOT_SYNCED_NOTICE} />
    }
    if (isNil(searchResult)) {
      return <Loader centered label={SEARCHING_LABEL} />
    }

    const { data, error } = searchResult
    if (!isNil(error)) {
      return <ErrorMessage content={error.message ?? SEARCH_FAILED_COPY} />
    }

    const searchTree = searchTreeOf(roots, data.matches)
    const rows = searchRowsOf({ roots, drives, searchTree, changes, cache })
    if (isEmpty(rows)) {
      return <NoData hideHeader text={NO_MATCHES_COPY} />
    }

    return (
      <>
        {data.truncated && <p className="muted-text">{TRUNCATED_COPY}</p>}
        <TreeTable
          key={`search:${debouncedQuery}`}
          {...treeProps}
          rows={toTreeRows(rows)}
          defaultExpandAll
        />
      </>
    )
  }

  const renderBody = () => {
    if (isNil(drives)) {
      return drivesFailed ? <ErrorMessage content={DRIVES_FAILED_COPY} /> : <Loader centered />
    }

    return (
      <>
        <div className="search-input mb">
          <TextInput
            className="fs-exclude"
            type="text"
            maxLength={MAX_SEARCH_LENGTH}
            aria-label={SEARCH_LABEL}
            placeholder={SEARCH_LABEL}
            data-testid="manage-files-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <IconSearch />
        </div>
        {inSearchMode ? (
          renderSearchResults()
        ) : (
          <TreeTable
            key={`browse:${treeVersion}`}
            {...treeProps}
            rows={toTreeRows(browseRowsOf({ roots, drives, cache, changes }))}
            onExpand={(row) => {
              if (!isNil(row.loadTarget)) {
                loadChildren(row.loadTarget)
              }
            }}
          />
        )}
      </>
    )
  }

  const footer = footerOf(changes)

  return (
    <>
      <BasicModal
        show
        size="large"
        scrolling
        title={TITLE}
        data-testid="manage-files-modal"
        onClose={requestClose}
        footer={
          <>
            {!isNil(footer.note) && <span className="muted-text">{footer.note}</span>}
            <Button className="ml-auto" onClick={requestClose}>
              Cancel
            </Button>
            <Button
              brand
              loading={saving}
              disabled={!footer.canSave || !isNil(finishing)}
              onClick={save}
            >
              Update
            </Button>
          </>
        }
      >
        {/* File names can carry PHI, in text and attributes; keep them out of session replays. */}
        <div className="fs-exclude" data-testid="manage-files-body">
          <p className="field-help">{subtitle}</p>
          {!isNil(message) && (
            <ErrorMessage className="mb" content={message.text} warning={message.warning} />
          )}
          {renderBody()}
        </div>
      </BasicModal>
      <ConfirmModal
        show={confirmingDiscard}
        onClose={() => setConfirmingDiscard(false)}
        title={DISCARD_TITLE}
        confirmLabel={DISCARD_LABEL}
        destructive
        onConfirm={() => onClose()}
        testIdPrefix="manage-files-discard"
      >
        <p>{DISCARD_BODY}</p>
      </ConfirmModal>
    </>
  )
}
